'use client'

import React, { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';
import { motion, Variants } from 'framer-motion';

import { Icon } from '../ui/Icon';
import { ParagraphSection } from '@/types/drupal';
import { trackEvent } from '@/lib/analytics';

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.8, 
      ease: [0.21, 0.47, 0.32, 0.98] 
    } 
  }
};

export default function Services({ data }: { data: ParagraphSection }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: false,
    breakpoints: {
      '(min-width: 1024px)': { slidesToScroll: 2 },
    }
  });

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const toggleExpand = (index: number) => {
    const isExpanding = expandedIndex !== index;
    setExpandedIndex(isExpanding ? index : null);
    if (isExpanding && data.services?.[index]) {
      trackEvent('service_expand', { service_name: data.services[index].title });
    }
  };

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    queueMicrotask(() => onSelect(emblaApi));
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  return (
    <section
      id="services"
      className="Services my-[30px] lg:py-[50px] bg-gradient-to-b from-brand-primary via-brand-deep to-brand-primary overflow-hidden"
      aria-label="Services"
      role="region"
      aria-roledescription="carousel"
    >
      <div className="lg:border-t lg:border-b lg:border-[#D9D9D9]">
        <div className="site-container">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-12 gap-0"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <motion.div
              variants={cardVariants}
              className="
                relative flex flex-col justify-center items-center lg:items-start 2xl:items-center
                lg:col-span-4 z-20
                pt-16 pb-20 lg:py-0 lg:min-h-[500px] px-6 lg:px-20 bg-[#0A4492]
                border-b lg:border-b-0 border-l lg:border-r border-[#D9D9D9]
              "
            >
              <div
                className="Services__heading section-heading text-center lg:text-left max-w-[300px]"
                dangerouslySetInnerHTML={{ __html: data.title?.processed || '' }}
              ></div>

              <div className="absolute bottom-5 lg:bottom-10 right-5 lg:right-10 flex gap-6">
                <button
                  onClick={scrollPrev}
                  aria-label="Previous services"
                  disabled={!canScrollPrev}
                  className={`transition-all cursor-pointer ${canScrollPrev ? 'opacity-100 scale-110' : 'opacity-30 cursor-not-allowed'}`}
                  style={{ color: '#BECEDF' }}
                >
                  <Icon name="ChevronLeft" size={28} strokeWidth={2} />
                </button>
                <button
                  onClick={scrollNext}
                  aria-label="Next services"
                  disabled={!canScrollNext}
                  className={`transition-all cursor-pointer ${canScrollNext ? 'opacity-100 scale-110' : 'opacity-30 cursor-not-allowed'}`}
                  style={{ color: '#B2FEFF' }}
                >
                  <Icon name="ChevronRight" size={28} strokeWidth={2} />
                </button>
              </div>
            </motion.div>

            <div className="lg:col-span-8 overflow-hidden pb-6 lg:pb-0 border-b lg:border-b-0 lg:border-r border-[#D9D9D9]" ref={emblaRef}>
              <div className="flex">
                {data.services?.map((service, index) => {
                  const isExpanded = expandedIndex === index;

                  return (
                    <motion.div
                      key={index}
                      variants={cardVariants}
                      role="group"
                      aria-roledescription="slide"
                      aria-label={`Service: ${service.title}`}
                      className="
                        flex flex-col items-center flex-none
                        w-[70%] sm:w-1/2 lg:w-1/2
                        pt-12 pb-6 lg:py-24 px-4
                        border-b lg:border-b-0 border-r border-[#D9D9D9]
                      "
                    >
                      <div className="relative">
                        <button
                          onClick={() => toggleExpand(index)}
                          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${service.title}`}
                          aria-expanded={isExpanded}
                          className={`
                            absolute -top-3 -right-3 h-10 w-10 rounded-full
                            flex items-center justify-center shadow-2xl z-40
                            cursor-pointer transition-all duration-500
                            ${isExpanded
                              ? 'bg-[#0A4492] text-white rotate-[135deg]'
                              : 'bg-white text-[#0A4492] hover:scale-110'}
                          `}
                        >
                          <Icon name="Plus" size={20} />
                        </button>

                        <div className="relative w-[190px] sm:w-[230px] lg:w-[340px] xs:w-[320px] h-[190px] xs:h-[320px] lg:h-[340px] overflow-hidden rounded-[15px]">
                          {service.icon?.mediaImage && (
                            <Image
                              src={service.icon.mediaImage.url}
                              alt={service.icon.mediaImage.alt || ''}
                              fill
                              priority={index < 2}
                              className={`object-cover transition-transform duration-700 ease-out
                                ${isExpanded ? 'scale-110 blur-[2px]' : 'scale-100'}`}
                              sizes="(max-width: 768px) 190px, (max-width: 1280px) 230px, 340px"
                            />
                          )}

                          <div
                            className={`
                              absolute inset-0 p-8 lg:p-10 flex flex-col justify-center
                              bg-[#BECEDF]/90 backdrop-blur-md
                              transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]
                              ${isExpanded
                                ? 'opacity-100 translate-y-0 visible'
                                : 'opacity-0 translate-y-12 invisible'}
                            `}
                          >
                            <h4 className={`text-[#0A4492] font-bold text-lg lg:text-xl mb-4 leading-tight transition-all duration-500 delay-100
                              ${isExpanded ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                              {service.title}
                            </h4>

                            <div
                              aria-hidden="true"
                              className={`
                                hidden lg:block h-0.5 bg-[#0A4492] lg:mb-6 transition-all duration-700 delay-200
                                ${isExpanded ? 'w-10 opacity-100' : 'w-0 opacity-0'}
                              `}
                            ></div>

                            <p
                              className={`
                                hidden lg:block text-[#0A4492] text-xs lg:text-sm leading-relaxed overflow-y-auto transition-all duration-500 delay-300
                                ${isExpanded ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
                              `}
                            >
                              {service.shortDescription}
                            </p>
                          </div>
                        </div>

                        <div className="text-center w-full max-w-[160px] sm:max-w-[220px] lg:max-w-[280px] mx-auto mt-6 lg:mt-10">
                          <h3 className="text-base lg:text-[22px] leading-4 lg:leading-6 text-white font-medium">
                            {service.title}
                          </h3>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
